import { FC, useContext, useEffect, useState } from "react";
import { Context } from "../../../index";
import "./SessionManager.css";
import { AppContext } from "../../../App";
import { IUserSessionForView } from "../../../models/PersonModels/IUserSessionForView";
import { Table, TableBody, TableHead, TableTd, TableTh, TableThSort, TableTr } from "../../UiLib/Table/Table";
import PersonLineDisplay from "../../Shared/PersonLineDisplay";
import { fullNameFormat } from "../../../helpers/Inicials";
import Button from "../../UiLib/Button/Button";
import canselGreey from "../../../assets/cancelGreey.svg";
import arrowUp from "../../../assets/arrow_up.svg";
import arrowDown from "../../../assets/arrow_down.svg";
import Search from "../../UiLib/Search/Search";
import ConfirmationWindow from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import axios from "axios";

const SessionManager: FC = (index) => {
    const { store } = useContext(Context);
    const [allUserSessions, setAllUserSessions] = useState<IUserSessionForView[]>([]);
    const [actualTime, setActualTime] = useState<Date>(new Date());
    const [searchValue, setSearchValue] = useState<string>("");
    const [idsSessionForDrop, setIdsSessionForDrop] = useState<string[]>([]);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [sortType, setSortType] = useState<boolean>(true);
    const [columnName, setColumnName] = useState<string>();

    const { showToast } = useContext(AppContext);

    useEffect(() => {
        (async () => {
            await getUsersSession();
        })();
    }, []);

    const calcTime = (msec: number): string => {
        const totalSeconds = Math.floor(msec / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts = [];
        if (hours > 0) parts.push(`${hours} ч.`);
        if (minutes > 0) parts.push(`${minutes} мин.`);
        if (seconds > 0 || parts.length === 0) parts.push(`${seconds} сек.`);

        return parts.join(" ");
    };

    const getUsersSession = async () => {
        try {
            const allSessions = await store.userService.getSessions();

            const now = new Date();

            setActualTime(new Date());
            let parsedSession = allSessions?.data.flatMap((xx) => {
                return xx.sessions.flatMap((yy) => {
                    return {
                        name: fullNameFormat(xx.user, "s N M"),
                        userId: xx.user.id,
                        role: xx.user.systemRole,
                        photoId: xx.user.photoId,
                        session: yy.session,
                        timeMsec: new Date(yy.dateEnd).getTime() - now.getTime(),
                        timeStr: calcTime(new Date(yy.dateEnd).getTime() - now.getTime()),
                        lic: yy.licenses,
                    };
                });
            });
            setAllUserSessions(parsedSession);
        } catch (ex: any) {
            if (axios.isAxiosError(ex)) {
                showToast(ex.response?.data.message);
            }
        }
    };

    const openConfirmation = (id: string) => {
        setShowConfirmation(true);
        setIdsSessionForDrop([id]);
    };

    const closeConfirmation = () => {
        setShowConfirmation(false);
        setIdsSessionForDrop([]);
    };

    const dropSession = async () => {
        try {
            let idsSession = [...idsSessionForDrop];

            await store.userService.deleteSession(idsSession);

            await getUsersSession();
            closeConfirmation();
        } catch (ex: any) {
            if (axios.isAxiosError(ex)) {
                showToast(ex.response?.data.message);
            }
        }
    };

    const search = (text: any) => {
        setSearchValue(text);
    };

    const handleUpdate = (name: string, sortTypeHandle: boolean) => {
        if (name !== columnName) {
            setSortType(true);
        } else setSortType(sortTypeHandle);
        setColumnName(name);
    };

    function sort(_rows: IUserSessionForView[]) {
        let sortFunc = (_a: any, _b: any) => 1;
        switch (columnName) {
            case "time":
                    sortFunc = (a, b) => (sortType ? a.timeMsec - b.timeMsec : b.timeMsec - a.timeMsec);
                break;
            case "name":
                sortFunc = (a, b) => (sortType ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
                break;
            case "session":
                sortFunc = (a, b) =>
                    sortType ? b.session.localeCompare(a.session) : a.session.localeCompare(b.session);
                break;
            case "role":
                sortFunc = (a, b) => (sortType ? b.role.localeCompare(a.role) : a.role.localeCompare(b.role));
                break;
        }

        allUserSessions.sort(sortFunc);

        let rows: IUserSessionForView[] = allUserSessions;
        rows.sort(sortFunc);

        return (
            <>
                {rows?.length > 0
                    ? rows
                          .filter(
                              (xx) =>
                                  xx.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                  xx.role.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
                          )
                          .map((xx) => {
                              xx.lic.sort((a, b) => +(a.licenseType === "nothing") - +(b.licenseType === "nothing"));
                              return (
                                  <TableTr style={{}}>
                                      <TableTd>
                                          <PersonLineDisplay name={xx.name} userId={xx.userId} />
                                      </TableTd>
                                      <TableTd>{xx.role}</TableTd>
                                      <TableTd>{xx.session}</TableTd>
                                      <TableTd>
                                          {xx.lic.map((yy: { licenseType: string }) => {
                                              return yy.licenseType === "conc" ? (
                                                  <div>Конкурентная</div>
                                              ) : yy.licenseType === "named" ? (
                                                  <div>Именная</div>
                                              ) : (
                                                  <div></div>
                                              );
                                          })}
                                      </TableTd>
                                      <TableTd>
                                          {xx.lic.map((yy: { licenseName: string }) => {
                                              return <div style={{ whiteSpace: "nowrap" }}>{yy.licenseName}</div>;
                                          })}
                                      </TableTd>
                                      <TableTd classBlock={"remaining-time"}>
                                          <div style={{ whiteSpace: "nowrap" }}>{xx.timeStr}</div>
                                      </TableTd>
                                      <TableTd>
                                          <Button
                                              className={"delete-session-btn"}
                                              callBack={() => openConfirmation(xx.session)}
                                              image={canselGreey}
                                          />
                                      </TableTd>
                                  </TableTr>
                              );
                          })
                    : null}
            </>
        );
    }

    return (
        <div className="session__settings">
            <div>
                <Search value={searchValue} changeSearchValue={search} className="session__search"></Search>
            </div>
            {showConfirmation ? (
                <ConfirmationWindow
                    headerText=""
                    bodyText="Закрыть сессию?"
                    onCancel={() => closeConfirmation()}
                    onConfirm={() => dropSession()}
                    cancelText="Отмена"
                    confirmText="Да"
                ></ConfirmationWindow>
            ) : null}
            <div className="session-container-table">
                <Table classBlock="session-table">
                    <TableHead classBlock={"session-table__header"}>
                        <TableTr>
                            <TableThSort callBack={() => handleUpdate("name", !sortType)} images={[arrowUp, arrowDown]}>
                                ПОЛЬЗОВАТЕЛЬ
                            </TableThSort>
                            <TableThSort callBack={() => handleUpdate("role", !sortType)} images={[arrowUp, arrowDown]}>
                                РОЛЬ
                            </TableThSort>
                            <TableThSort
                                callBack={() => handleUpdate("session", !sortType)}
                                images={[arrowUp, arrowDown]}
                            >
                                СЕССИЯ
                            </TableThSort>
                            <TableTh>ВИД ЛИЦЕНЗИИ</TableTh>
                            <TableTh>ТИП ЛИЦЕНЗИИ</TableTh>
                            <TableThSort
                                callBack={() => handleUpdate("time", !sortType)}
                                images={[arrowUp, arrowDown]}
                                style={{ textAlign: "left" }}
                            >
                                ОСТАВШЕЕСЯ ВРЕМЯ ЖИЗНИ СЕССИИ
                            </TableThSort>
                            <TableTh></TableTh>
                        </TableTr>
                    </TableHead>
                    <TableBody>{sort(allUserSessions)}</TableBody>
                </Table>
            </div>
        </div>
    );
};

export default SessionManager;
